import { useLingui } from '@lingui/react/macro'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import { HiddenFileInput } from 'designSystem/components/HiddenFileInput'
import { Spinner } from 'designSystem/components/Spinner'
import { Text } from 'designSystem/components/Text'
import {
  BUG_REPORT_SCREENSHOT_ACCEPT,
  type BugReportScreenshot,
  MAX_BUG_REPORT_SCREENSHOTS,
  type ScreenshotErrorCode,
} from 'features/knowledgeDocumentViewer/utils/bugReportScreenshots'
import { ImagePlus, X } from 'lucide-react'
import { type ChangeEvent, useCallback, useRef } from 'react'

import {
  Caption,
  Image,
  Preview,
  Previews,
  Remove,
} from './bugReportScreenshots.styles'

type Props = {
  screenshots: BugReportScreenshot[]
  operation: 'files' | null
  error: ScreenshotErrorCode | null
  disabled: boolean
  onFiles: (files: File[]) => void
  onRemove: (id: string) => void
}

export function BugReportScreenshots({
  screenshots,
  operation,
  error,
  disabled,
  onFiles,
  onRemove,
}: Props) {
  const { t } = useLingui()
  const input = useRef<HTMLInputElement>(null)
  const chooseFiles = useCallback(() => input.current?.click(), [])
  const handleFiles = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onFiles(Array.from(event.target.files ?? []))
      event.target.value = ''
    },
    [onFiles]
  )
  const errors: Record<ScreenshotErrorCode, string> = {
    type: t`Attach PNG, JPEG, or GIF images.`,
    size: t`Each screenshot must be a non-empty image of 5 MB or less.`,
    read: t`Could not read that image. Please try another file.`,
    limit: t`You can attach up to 3 screenshots. Remove one before adding more.`,
  }
  return (
    <Box display='grid' gap='sm' asChild>
      <section aria-label={t`Screenshots`}>
        <Text size='sm' weight='semibold'>{t`Screenshots`}</Text>
        <Text size='xs' color='muted'>
          {t`Attach or paste screenshots to help explain the issue. Images are sent to Gecko Support when you submit.`}
        </Text>
        <Previews>
          {screenshots.map(({ id, filename, previewUrl }) => (
            <Preview key={id}>
              <Image src={previewUrl} alt={t`Screenshot: ${filename}`} />
              <Remove>
                <Button
                  type='button'
                  variant='inputRaised'
                  size='icon-sm'
                  disabled={disabled}
                  aria-label={t`Remove ${filename}`}
                  onClick={() => onRemove(id)}>
                  <X size={14} />
                </Button>
              </Remove>
              <Caption>
                <Text as='div' size='xs' truncate title={filename}>
                  {filename}
                </Text>
              </Caption>
            </Preview>
          ))}
        </Previews>
        <HiddenFileInput
          ref={input}
          multiple
          accept={BUG_REPORT_SCREENSHOT_ACCEPT}
          aria-label={t`Attach screenshots`}
          onChange={handleFiles}
          disabled={disabled || operation !== null}
        />
        <Button
          type='button'
          variant='inputRaised'
          size='sm'
          onClick={chooseFiles}
          disabled={
            disabled ||
            operation !== null ||
            screenshots.length >= MAX_BUG_REPORT_SCREENSHOTS
          }>
          {operation === 'files' ? (
            <Spinner size={16} />
          ) : (
            <ImagePlus size={16} />
          )}
          {t`Attach screenshots`}
        </Button>
        <Text
          size='xs'
          color='muted'>{t`Or paste an image. Up to 3 images, 5 MB each.`}</Text>
        {error ? (
          <Text size='sm' color='destructive' role='alert'>
            {errors[error]}
          </Text>
        ) : null}
      </section>
    </Box>
  )
}
